/**
 * Layout dimension constants for Reports Domain
 *
 * Centralized constants for all layout calculations to ensure consistency
 * across the reports domain. If layout changes in the future, only this
 * file needs to be updated.
 *
 * Layout structure:
 * - Mobile: Black navbar (56px) + TopNav (56px) + optional Banner (56px)
 * - Desktop: TopNav (56px) + optional Banner (56px)
 */

/**
 * Top navigation bar height (blue bar with store/organization context)
 * Fixed height on both mobile and desktop
 */
export const TOP_NAV_HEIGHT = 56;

/**
 * Mobile black navbar height
 * Only visible on mobile devices (< 640px)
 */
export const MOBILE_NAVBAR_HEIGHT = 56;

/**
 * Banner height when publish menu error is shown
 * Displayed inside TopNavigationBarStores when validation fails
 */
export const BANNER_HEIGHT = 56;

/**
 * Sidebar widths for desktop layout
 */
export const SIDEBAR_WIDTH_EXPANDED = 260;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

/**
 * Breakpoint for mobile/desktop distinction
 * Matches Tailwind's 'sm' breakpoint
 */
export const MOBILE_BREAKPOINT = 640;

/**
 * Calculate total top offset for workspace
 *
 * @param showBanner - Whether the banner is currently displayed
 * @returns Object with pixel values for mobile and desktop
 */
export const calculateTopOffset = (showBanner: boolean) => {
  return {
    mobile: MOBILE_NAVBAR_HEIGHT + TOP_NAV_HEIGHT + (showBanner ? BANNER_HEIGHT : 0),
    desktop: TOP_NAV_HEIGHT + (showBanner ? BANNER_HEIGHT : 0),
  };
};

/**
 * Generate Tailwind CSS classes for top margin
 *
 * @param showBanner - Whether the banner is currently displayed
 * @returns Tailwind class string for margin-top
 */
export const getTopMarginClasses = (showBanner: boolean): string => {
  return showBanner
    ? 'mt-[168px] sm:mt-[112px]' // Mobile: 56+56+56=168px, Desktop: 56+56=112px
    : 'mt-[112px] sm:mt-[56px]'; // Mobile: 56+56=112px, Desktop: 56px
};

/**
 * Generate Tailwind CSS classes for viewport height calculation
 *
 * @param showBanner - Whether the banner is currently displayed
 * @returns Tailwind class string for height
 */
export const getHeightClasses = (showBanner: boolean): string => {
  return showBanner
    ? 'h-[calc(100vh-168px)] sm:h-[calc(100vh-112px)]'
    : 'h-[calc(100vh-112px)] sm:h-[calc(100vh-56px)]';
};

/**
 * Generate Tailwind CSS classes for sticky positioning
 * Used for sticky table headers and other fixed elements
 *
 * @param showBanner - Whether the banner is currently displayed
 * @returns Tailwind class string for sticky top position
 */
export const getStickyTopClasses = (showBanner: boolean): string => {
  return showBanner ? 'top-[168px] sm:top-[112px]' : 'top-[112px] sm:top-[56px]';
};

/**
 * Calculate sidebar-aware width for top navigation
 *
 * @param sidebarExpanded - Whether the sidebar is expanded (260px) or collapsed (64px)
 * @returns Tailwind class string for width calculation
 */
export const getTopNavWidthClasses = (sidebarExpanded: boolean): string => {
  return sidebarExpanded ? 'sm:w-[calc(100%-260px)]' : 'sm:w-[calc(100%-64px)]';
};
/**
 * Calculate sidebar-aware width for workspace
 *
 * @param sidebarExpanded - Whether the sidebar is expanded (260px) or collapsed (64px)
 * @returns Tailwind class string for viewport width calculation
 */
export const getWorkspaceWidthClasses = (sidebarExpanded: boolean): string => {
  return sidebarExpanded ? 'sm:w-[calc(100vw-260px)]' : 'sm:w-[calc(100vw-64px)]';
};
